import React, { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { InputErrorMessage } from '@savvy-kit/components/input-error-message';
import { cn } from '@savvy-kit/lib/utils';

const BOX_ELEMENTS_VERSION = '14.0.0';
const BOX_PREVIEW_JS = `https://cdn01.boxcdn.net/platform/elements/${BOX_ELEMENTS_VERSION}/en-US/preview.js`;
const BOX_PREVIEW_CSS = `https://cdn01.boxcdn.net/platform/elements/${BOX_ELEMENTS_VERSION}/en-US/preview.css`;

type Props = {
  readonly accessToken: string | null | undefined;
  readonly fileId: string;
  readonly advisorView?: boolean;
  readonly hideFileName?: boolean;
  readonly showHeader?: boolean;
  readonly disableMaxWidth?: boolean;
};

function BoxContentPreviewer({
  accessToken,
  fileId,
  advisorView = false,
  hideFileName = false,
  showHeader = true,
  disableMaxWidth = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [boxReady, setBoxReady] = useState(() => typeof window !== 'undefined' && !!window.Box);
  const [scriptError, setScriptError] = useState(false);

  useEffect(() => {
    if (!boxReady || !fileId || !accessToken || !containerRef.current) return;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by boxReady state
    const preview = new window.Box!.ContentPreview();

    preview.show(fileId, accessToken, {
      container: containerRef.current,
      hasHeader: showHeader,
      showDownload: true,
      logoUrl: hideFileName ? 'Savvy' : '',
    });

    return () => {
      try {
        preview.hide();
      } catch {
        // Box SDK may throw if preview wasn't fully initialized
      }
    };
  }, [fileId, accessToken, boxReady, showHeader, hideFileName]);

  return (
    <main className="h-full grow pb-0">
      <link rel="stylesheet" href={BOX_PREVIEW_CSS} />
      <Script
        src={BOX_PREVIEW_JS}
        strategy="afterInteractive"
        onReady={() => setBoxReady(true)}
        onError={() => {
          setScriptError(true);
          console.error(`FE Box Error [Files] Script error: ${BOX_PREVIEW_JS}`);
        }}
      />
      <div className={cn('mx-auto h-full w-full', !disableMaxWidth && 'max-w-[1920px]')}>
        <div className={cn('h-full', advisorView ? 'mb-0' : 'mb-8')}>
          <div className={`grid h-full grid-cols-12 justify-between ${advisorView ? '' : 'mb-4'}`}>
            <div className="col-span-12 h-full">
              {scriptError && (
                <div className="m-4">
                  <InputErrorMessage message="Could not load files via Box. Please check if Box is being blocked by a VPN, corporate proxy, or browser extension." />
                </div>
              )}
              <div ref={containerRef} style={{ height: '100%' }} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default BoxContentPreviewer;
